import { useMemo, useState } from "react";
import { telLink, whatsappLink } from "@chbapark/domain/normalization";
import { BackLink } from "../ui/BackLink";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Avatar } from "../ui/Avatar";
import { LogContactSheet } from "../ui/LogContactSheet";
import { AddDonationSheet } from "../ui/AddDonationSheet";
import { SubmissionSheet } from "../ui/SubmissionSheet";
import {
  daysSinceContact,
  useHousehold,
  usePeopleByIds,
  usePeopleSearch,
  usePerson,
  usePersonTimeline,
  useTagDefs,
  primaryEmail,
  primaryPhone,
  type Person,
  type TimelineEntry,
} from "../data";
import {
  OWNERS,
  deletePeople,
  linkDuplicateAsFamily,
  logInteraction,
  setFamilyLink,
  setPersonTags,
  updatePerson,
} from "../actions";
import { EditPersonSheet } from "../ui/EditPersonSheet";
import { MergeWithCard } from "../ui/MergeWithCard";
import { TagChip, TagPicker } from "../ui/TagPicker";
import { useAuth } from "../auth";
import {
  IcAlert,
  IcCalendar,
  IcCheck,
  IcChevron,
  IcClock,
  IcHome,
  IcMerge,
  IcPeople,
  IcPhone,
  IcPlus,
  IcSearch,
  IcSpark,
  IcTrash,
  IcWallet,
  IcWhatsapp,
} from "../ui/icons";

/**
 * The person card (spec §4.4): a useful summary rather than a form of dozens of
 * fields — contact details, the family, interests, timeline and a next step.
 *
 * Two things the product owner asked for are reflected here directly.
 *
 * **Consents are not shown.** The spec lists them on this card, and they are
 * still recorded and still checked wherever a message is actually sent. But a
 * row reading "דיוור: לא נשאל" on three thousand imported records is noise on
 * every single card, and he asked for it gone. Consent belongs at the moment of
 * sending, which is where it will be enforced.
 *
 * **Tags come off.** Every tag now has a remove control. A label that arrived
 * from a form in 2019 — "ליד בר מצווה" — was permanent, and no amount of
 * hiding the definition in Settings took it off the person carrying it.
 */
export function PersonPage() {
  const { personId } = useParams();
  const navigate = useNavigate();
  const { role } = useAuth();
  const { person, loading, error: personError } = usePerson(personId);
  const timeline = usePersonTimeline(personId);
  const tagDefs = useTagDefs("person");
  const services = useTagDefs("business_service");
  const { household, error: householdError } = useHousehold(person?.householdId);
  const familyMembers = usePeopleByIds(household?.memberIds);
  const duplicates = usePeopleByIds(person?.sharesPhoneWithPersonIds);

  /**
   * Opened straight from the quick-add sheet.
   *
   * `?donate=1` and `?log=1` mean the person was already chosen somewhere else
   * and the form should be up on arrival. Without this the "+" button could only
   * ever land you on a card and leave you to find the right button on it —
   * which is how recording a cash donation stayed effectively unreachable even
   * after it was built and deployed.
   */
  const [params, setParams] = useSearchParams();
  const [logging, setLogging] = useState(params.get("log") === "1");
  const [donating, setDonating] = useState(params.get("donate") === "1");
  const [editing, setEditing] = useState(false);
  const [editingTags, setEditingTags] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [mergeTarget, setMergeTarget] = useState<{ id: string; name: string } | null>(null);
  /** Set when a call or a message was started from here, so the app can ask what
   *  happened — the one thing no phone API will tell it. */
  const [reached, setReached] = useState<"call" | "whatsapp" | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  /** Which website submission is open, if any. */
  const [openSubmission, setOpenSubmission] = useState<string | null>(null);

  const isAdmin = role === "owner" || role === "admin";
  /** Recording money is staff work, not an admin-only act — the person who took
   *  the envelope is the one who should be able to write it down. */
  const isStaff = isAdmin || role === "staff";

  /** Drops the flag off the address once the sheet it opened is closed, so a
   *  refresh or a back-navigation does not reopen a form the user just dismissed. */
  function closeParam(key: string) {
    if (!params.has(key)) return;
    const next = new URLSearchParams(params);
    next.delete(key);
    setParams(next, { replace: true });
  }

  const tagNameById = useMemo(
    () => new Map([...tagDefs.data, ...services.data].map((t) => [t.id, t.name ?? ""])),
    [tagDefs.data, services.data],
  );

  if (loading) {
    return (
      <div className="screen">
        <div className="body">
          <div className="skeleton" style={{ height: 90, marginBottom: 12 }} />
          <div className="skeleton" style={{ height: 200 }} />
        </div>
      </div>
    );
  }

  if (!person) {
    return (
      <div className="screen">
        <div className="body">
          <BackLink fallback="/people" label="חזרה" />
          {/* A failed read is not the same statement as "there is nobody here",
              and saying the second when the first happened is how the empty
              timeline went unnoticed for as long as it did. */}
          {personError ? (
            <div className="notice error">
              לא הצלחנו לטעון את איש הקשר. זו תקלה או בעיית הרשאה. זה לא אומר שאיש הקשר לא קיים.
              <br />
              <span className="faint faint-xs mono">
                {personError}
              </span>
            </div>
          ) : (
            <div className="empty">
              <div className="empty-ico">
                <IcPeople size={26} />
              </div>
              <h3>איש הקשר לא נמצא</h3>
              <p>אולי הוא נמחק, אוחד עם כרטיס אחר או הועבר לארכיון.</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  const name = person.displayName ?? "ללא שם";
  const phone = primaryPhone(person);
  const email = primaryEmail(person);
  const carried = [...(person.tagIds ?? []), ...(person.businessServiceTagIds ?? [])];
  const days = daysSinceContact(person);

  async function removeTag(tagId: string) {
    if (!personId) return;
    setBusy(true);
    setErr(null);
    try {
      await setPersonTags(personId, { remove: [tagId] });
    } catch (e) {
      setErr(e instanceof Error ? e.message : "לא הצלחנו להסיר את התגית. נסו שוב.");
    } finally {
      setBusy(false);
    }
  }

  /**
   * Turns "I tapped call" into a timeline entry, once the human says what
   * happened. `answered` is the whole point: a call that rang out is not contact,
   * and recording it as one would tell the engine this person has been attended
   * to when nobody spoke to them.
   */
  async function recordReach(kind: "call" | "whatsapp", answered: boolean) {
    if (!personId) return;
    setBusy(true);
    setErr(null);
    try {
      await logInteraction({
        personId,
        type: kind,
        channel: kind === "call" ? "phone" : "whatsapp",
        direction: "outbound",
        summary: answered
          ? kind === "call"
            ? "שיחת טלפון"
            : "הודעת וואטסאפ"
          : "התקשרתי ולא ענה",
        // Only a conversation counts as contact.
        meaningful: answered,
      });
      setReached(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "לא הצלחנו לשמור. נסו שוב.");
    } finally {
      setBusy(false);
    }
  }

  async function toggleOwner(slug: string) {
    if (!person) return;
    const current = person.owners ?? [];
    const next = current.includes(slug)
      ? current.filter((o) => o !== slug)
      : [...current, slug];
    setBusy(true);
    setErr(null);
    try {
      await updatePerson({ personId: person.id, owners: next });
    } catch (e) {
      setErr(e instanceof Error ? e.message : "לא הצלחנו לשנות את השיוך. נסו שוב.");
    } finally {
      setBusy(false);
    }
  }

  async function addTag(tagId: string) {
    if (!personId) return;
    setBusy(true);
    setErr(null);
    try {
      await setPersonTags(personId, { add: [tagId] });
    } catch (e) {
      setErr(e instanceof Error ? e.message : "לא הצלחנו להוסיף את התגית. נסו שוב.");
    } finally {
      setBusy(false);
    }
  }

  async function doDelete(force: boolean) {
    if (!personId) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await deletePeople([personId], force);
      if (r.blockedByVerifiedDonations.length > 0) {
        setErr(
          "לאיש הקשר יש תרומות מאומתות מספק הסליקה. אם תמחקו אותו, הכסף יישאר בלי שיוך. אם זו הכוונה, אשרו שוב.",
        );
        setBusy(false);
        return;
      }
      navigate("/people", { replace: true });
    } catch (e) {
      setErr(e instanceof Error ? e.message : "לא הצלחנו למחוק. נסו שוב.");
      setBusy(false);
    }
  }

  return (
    <div className="screen">
      <div className="body">
        <BackLink fallback="/people" label="חזרה" />

        <div className="person-hero">
          <Avatar name={name} size={62} />
          <div className="grow">
            <h1>{name}</h1>
            <p className="faint" style={{ marginTop: 4 }}>
              {sourceLabel(person.source?.type)}
              {person.householdRole && ` · ${roleLabel(person.householdRole)}`}
              {days != null && ` · דיברתם ${days === 0 ? "היום" : `לפני ${days} ימים`}`}
              {person.isDemo && " · איש קשר לדוגמה"}
            </p>
          </div>
        </div>

        {/* Two facts about the record itself, stated where they are relevant
            rather than left for the reader to work out from a search result. */}
        {person.isCoupleRecord && (
          <div className="notice info" style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <IcPeople size={17} />
            <span>
              בכרטיס הזה רשום שם של זוג, לא של אדם אחד. שמרנו אותו כמו שהגיע. אפשר להשאיר
              אותו ככרטיס של המשפחה.
            </span>
          </div>
        )}

        {duplicates.data.length > 0 && (
          <div className="notice error" style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
            <span style={{ flexShrink: 0, marginTop: 2 }}>
              <IcMerge size={17} />
            </span>
            <span>
              <b>אותו מספר טלפון רשום גם אצל:</b>
              <br />
              {duplicates.data.map((d, i) => (
                <span key={d.id}>
                  {i > 0 && " · "}
                  <Link to={`/people/${d.id}`} style={{ textDecoration: "underline" }}>
                    {d.displayName ?? "ללא שם"}
                  </Link>
                  {/* One tap to the merge preview for exactly this pair, instead of
                      sending the reader to a queue of 500 to find it again. */}
                  {isAdmin && (
                    <>
                      {" "}
                      <button
                        className="btn btn-sm"
                        onClick={() => {
                          setMergeTarget({ id: d.id, name: d.displayName ?? "" });
                          document.getElementById("merge-with")?.scrollIntoView({ behavior: "smooth" });
                        }}
                      >
                        איחוד לכאן
                      </button>
                    </>
                  )}
                </span>
              ))}
              <br />
              {/*
                This used to read "איחוד רשומות עוד לא נבנה — בינתיים אפשר למחוק
                את המיותרת", which was wrong twice over. Merging exists: it is
                `mergePeople`, deployed, and wired to the Duplicates screen. And
                the advice it gave instead — delete the spare — destroys the
                donations, timeline and tags on the record it tells you to throw
                away, which is precisely what merging exists to preserve.
              */}
              <span className="faint">
                אותו אדם שנרשם פעמיים? לחצו „איחוד לכאן”. האיחוד שומר את התרומות, ציר הזמן
                והתגיות של שני הכרטיסים. מחיקה לא שומרת אותם. אם זה בן משפחה עם אותו טלפון,
                אין צורך לעשות כלום.
              </span>
            </span>
          </div>
        )}

        {person.nextStepText && (
          <div className="card card-highlight">
            <div className="card-body row">
              <span
                className="rail-ico tint-orange"
              >
                <IcSpark size={18} />
              </span>
              <div className="grow">
                <p className="name-row">
                  הצעה לצעד הבא
                </p>
                <p className="muted">{person.nextStepText}</p>
              </div>
            </div>
          </div>
        )}

        <div className="quick-actions">
          <a
            className="qa"
            /**
             * The number is normalised here, not displayed here.
             *
             * `primaryPhone` returns the number as somebody typed it, which is
             * right on screen and wrong in a link: stripping the punctuation off
             * "050-866-6333" leaves WhatsApp a number with no country code, and
             * it refuses to open. `whatsappLink` applies the rule the record was
             * written with, and returns null rather than a link to nothing.
             */
            href={whatsappLink(phone) ?? undefined}
            target="_blank"
            rel="noreferrer"
            aria-disabled={!whatsappLink(phone)}
            style={whatsappLink(phone) ? undefined : { opacity: 0.45, pointerEvents: "none" }}
            onClick={() => phone && setReached("whatsapp")}
          >
            <span className="rail-ico tint-green">
              <IcWhatsapp />
            </span>
            WhatsApp
          </a>
          <a
            className="qa"
            href={telLink(phone) ?? undefined}
            aria-disabled={!telLink(phone)}
            style={telLink(phone) ? undefined : { opacity: 0.45, pointerEvents: "none" }}
            onClick={() => phone && setReached("call")}
          >
            <span
              className="rail-ico tint-orange"
            >
              <IcPhone />
            </span>
            התקשרו
          </a>
          <button className="qa" onClick={() => setLogging(true)}>
            <span className="rail-ico tint-slate">
              <IcClock />
            </span>
            תיעוד קשר
          </button>
        </div>

        {/*
          Asked because the phone cannot be read.
          No API on either platform exposes a personal call log to a web app —
          Android gates it behind a Play-restricted permission for dialler apps,
          iOS does not expose it at all. So the CRM knows the call was *started*
          from here and nothing more. One tap turns that into a real timeline
          entry; ignoring it costs nothing and claims nothing.
        */}
        {reached && personId && (
          <div className="card card-highlight">
            <div className="card-body">
              <p className="name-row">
                {reached === "call" ? "התקשרתם ל" : "שלחתם הודעה ל"}
                {name}. דיברתם?
              </p>
              <div className="btn-row" style={{ marginTop: 8 }}>
                <button
                  className="btn btn-primary"
                  disabled={busy}
                  onClick={() => recordReach(reached, true)}
                >
                  כן, דיברנו
                </button>
                <button className="btn" disabled={busy} onClick={() => recordReach(reached, false)}>
                  לא ענו
                </button>
                <button className="btn" disabled={busy} onClick={() => setReached(null)}>
                  דלגו
                </button>
              </div>
              <p className="faint" style={{ marginTop: 8 }}>
                „כן” נרשם בציר הזמן ומעדכן מתי דיברתם. „לא ענו” נרשם רק כניסיון.
              </p>
            </div>
          </div>
        )}

        {/* Greyed-out buttons with no explanation read as a broken screen. */}
        {!phone && (
          <p className="faint" style={{ padding: "0 4px 10px", marginTop: -4 }}>
            אין בכרטיס מספר טלפון, אז אי אפשר להתקשר או לשלוח וואטסאפ. כדי להוסיף מספר,
            לחצו „עריכה”.
          </p>
        )}

        {err && <div className="notice error">{err}</div>}

        <div className="detail-2col">
          <div>
            <div className="card">
              <div className="card-title">ציר הזמן</div>

              {/* A read that fails now says so. It used to render as "no activity
                  yet", which is why a man with fifteen donations looked like
                  somebody nobody had ever spoken to. */}
              {timeline.error ? (
                <div style={{ padding: "6px 14px 18px" }}>
                  <div className="notice error" style={{ margin: 0 }}>
                    לא הצלחנו לטעון את ציר הזמן. זו תקלה או בעיית הרשאה. זה לא אומר שאין פעילות.
                    <br />
                    <span className="faint faint-xs mono">
                      {timeline.error}
                    </span>
                  </div>
                </div>
              ) : timeline.loading ? (
                <div style={{ padding: 14 }}>
                  <div className="skeleton" style={{ height: 54 }} />
                </div>
              ) : timeline.entries.length === 0 ? (
                <div className="empty" style={{ padding: "28px 22px" }}>
                  <p>
                    עדיין אין פה פעילות. שיחות, נוכחות, תרומות והרשמות יופיעו כאן כשייכנסו
                    למערכת, או כשתתעדו קשר.
                  </p>
                </div>
              ) : (
                <div className="tl">
                  {timeline.entries.map((t) => (
                    <TimelineRow key={t.id} entry={t} onOpenSubmission={setOpenSubmission} />
                  ))}
                </div>
              )}
            </div>
          </div>

          <div>
            <div className="card">
              <div className="card-title" style={{ display: "flex", alignItems: "center" }}>
                <span className="grow">פרטי קשר</span>
                <button
                  className="faint"
                  onClick={() => setEditing(true)}
                  style={{ padding: "2px 6px" }}
                >
                  עריכה
                </button>
              </div>
              {/* Every number on file, not just the primary. A person with two
                  numbers had one of them invisible. */}
              {(person.phones ?? []).length > 1 ? (
                (person.phones ?? []).map((p, i) => (
                  <Field
                    key={p.value ?? i}
                    label={i === 0 ? "טלפון" : "טלפון נוסף"}
                    value={p.value ?? null}
                    mono
                  />
                ))
              ) : (
                <Field label="טלפון" value={phone} mono />
              )}
              <Field label="מייל" value={email} mono />
              <Field label="כתובת" value={addressLine(person)} />
              {person.organization && <Field label="עסק" value={person.organization} />}
            </div>

            {/* Whose contact this is. The import assigned all 3,893 — 2,854 to
                מענדי, 1,475 to שירה — and nothing displayed it, so the field
                existed and was invisible and uneditable. */}
            <div className="card">
              <div className="card-title">שיוך</div>
              <div className="card-pad">
                <div className="chips">
                  {OWNERS.map((o) => {
                    const on = (person.owners ?? []).includes(o.slug);
                    return (
                      <button
                        key={o.slug}
                        className={on ? "tagchip tagchip-on" : "tagchip"}
                        aria-pressed={on}
                        onClick={() => toggleOwner(o.slug)}
                        disabled={busy}
                      >
                        {o.label}
                      </button>
                    );
                  })}
                </div>
                {(person.owners ?? []).length === 0 && (
                  <p className="faint" style={{ marginTop: 8 }}>
                    עוד לא משויך לאף אחד. לחצו על שם כדי לשייך.
                  </p>
                )}
              </div>
            </div>

            <FamilyCard
              person={person}
              household={household}
              members={familyMembers.data}
              error={householdError ?? familyMembers.error}
            />

            <div className="card">
              <div className="card-title" style={{ display: "flex", alignItems: "center" }}>
                <span className="grow">תגיות</span>
                <button
                  className="faint"
                  onClick={() => setEditingTags((v) => !v)}
                  style={{ padding: "2px 6px" }}
                >
                  {editingTags ? "סיום" : "עריכה"}
                </button>
              </div>

              <div className="card-pad">
                {carried.length === 0 && !editingTags && <p className="muted">אין תגיות.</p>}

                <div className="chips">
                  {carried.map((id) => (
                    <TagChip
                      key={id}
                      id={id}
                      name={tagNameById.get(id) ?? "תגית לא מוכרת"}
                      busy={busy}
                      onRemove={editingTags ? () => void removeTag(id) : undefined}
                    />
                  ))}
                </div>

                {editingTags && (
                  <>
                    <p className="faint" style={{ marginTop: 12, marginBottom: 6 }}>
                      הוספה:
                    </p>
                    <TagPicker
                      selected={person.tagIds ?? []}
                      busy={busy}
                      onToggle={(id, on) => void (on ? addTag(id) : removeTag(id))}
                    />
                    <p className="faint" style={{ marginTop: 10 }}>
                      הסרה כאן משפיעה רק על איש הקשר הזה. כדי לבטל תגית אצל כולם, היכנסו להגדרות ← תגיות.
                    </p>
                  </>
                )}
              </div>
            </div>

            {/* Giving history, now that something computes it. Verified and
                imported are shown apart on purpose: one is confirmation from a
                payment provider, the other is what a spreadsheet said, and a
                single combined figure would be a number nobody could defend. */}
            {(person.donationCount ?? 0) > 0 && (
              <div className="card">
                <div className="card-title">תרומות</div>
                <Field
                  label="סה״כ"
                  value={
                    [
                      (person.donationTotalVerifiedMinor ?? 0) > 0
                        ? `${((person.donationTotalVerifiedMinor ?? 0) / 100).toLocaleString("he-IL")} ₪ מאומת`
                        : null,
                      (person.donationTotalImportedMinor ?? 0) > 0
                        ? `${((person.donationTotalImportedMinor ?? 0) / 100).toLocaleString("he-IL")} ₪ מהייבוא`
                        : null,
                    ]
                      .filter(Boolean)
                      .join(" · ") || null
                  }
                />
                <Field label="מספר תרומות" value={String(person.donationCount)} />
                <Field
                  label="תרומה אחרונה"
                  value={
                    person.lastDonationAt
                      ? new Date(person.lastDonationAt).toLocaleDateString("he-IL", {
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                        })
                      : null
                  }
                />
                {person.hasStandingOrder && (
                  <div style={{ padding: "0 14px 10px" }}>
                    <span className="rec-badge rec-family">הוראת קבע</span>
                  </div>
                )}
                <div className="card-pad">
                  <p className="faint">
                    סכומים מהייבוא הם מה שנרשם בגיליון. הם לא אישור מספק הסליקה.
                  </p>
                </div>
                {isStaff && (
                  <div className="btn-row card-pad">
                    <button className="btn" onClick={() => setDonating(true)}>
                      <IcPlus size={15} /> הוסיפו תרומה
                    </button>
                  </div>
                )}
              </div>
            )}

            {/*
              The same action where there is no card yet.

              Somebody's first cash donation is the most likely one to be typed
              in, and it was unreachable: the donations card only renders once a
              donation exists, so the button to add one lived inside the thing it
              was supposed to create.
            */}
            {(person.donationCount ?? 0) === 0 && isStaff && (
              <button className="card list-row" onClick={() => setDonating(true)}>
                <span
                  className="rail-ico tint-amber"
                >
                  <IcWallet size={17} />
                </span>
                <span className="grow">
                  <span className="name-row">
                    הוסיפו תרומה
                  </span>
                  <span className="muted">מזומן, העברה או צ׳ק שקיבלתם מחוץ למערכת</span>
                </span>
                <IcPlus size={17} />
              </button>
            )}

            {(person.gregorianBirthDate || person.education?.school) && (
              <div className="card">
                <div className="card-title">פרטים נוספים</div>
                <Field label="תאריך לידה" value={person.gregorianBirthDate ?? null} />
                <Field label="מסגרת חינוכית" value={person.education?.school ?? null} />
                <Field label="כיתה / שכבה" value={person.education?.grade ?? null} />
              </div>
            )}

            {isAdmin && personId && (
              <MergeWithCard personId={personId} target={mergeTarget} onTarget={setMergeTarget} />
            )}

            {isAdmin && (
              <div className="card">
                <div className="card-title">מחיקה</div>
                {deleting ? (
                  <div className="card-pad">
                    <p className="muted">
                      למחוק את <b>{name}</b> לגמרי? הכרטיס, ציר הזמן והשיוך למשפחה יימחקו.
                      התרומות לא נמחקות. הן יישארו בלי שיוך ויסומנו לבדיקה.
                    </p>
                    <p className="faint" style={{ marginTop: 6 }}>
                      אנחנו שומרים גיבוי מלא של הכרטיס ביומן הביקורת, אז אפשר לשחזר מחיקה בטעות.
                    </p>
                    <div className="btn-row" style={{ marginTop: 12 }}>
                      <button className="btn" onClick={() => doDelete(false)} disabled={busy}>
                        <span className="danger">{busy ? "מוחקים…" : "כן, למחוק"}</span>
                      </button>
                      <button className="btn" onClick={() => setDeleting(false)} disabled={busy}>
                        ביטול
                      </button>
                    </div>
                  </div>
                ) : (
                  <button className="list-row" onClick={() => setDeleting(true)}>
                    <span
                      className="rail-ico tint-red" style={{ width: 34, height: 34 }}
                    >
                      <IcTrash size={16} />
                    </span>
                    <span className="grow">
                      <span className="danger">מחיקת איש הקשר</span>
                      <span className="faint" style={{ display: "block", marginTop: 2 }}>
                        למי שעבר מהשכונה או נרשם בטעות
                      </span>
                    </span>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {logging && personId && (
        <LogContactSheet
          personId={personId}
          personName={name}
          onClose={() => {
            setLogging(false);
            closeParam("log");
          }}
        />
      )}
      {donating && personId && (
        <AddDonationSheet
          personId={personId}
          personName={name}
          onClose={() => {
            setDonating(false);
            closeParam("donate");
          }}
        />
      )}
      {editing && <EditPersonSheet person={person} onClose={() => setEditing(false)} />}
      {openSubmission && (
        <SubmissionSheet
          submissionId={openSubmission}
          onClose={() => setOpenSubmission(null)}
        />
      )}
    </div>
  );
}

/**
 * Family, and neighbours, told apart.
 *
 * The imported `household_id` groups people by street address: one household is
 * twenty-two people from five families, and its `father_name` is five men's
 * names in one comma-joined string. Printing that as "משפחת כהן · 8 בני בית"
 * would put seven strangers on somebody's card as their relatives, which the
 * spec forbids in as many words — *אסור להסיק קשר משפחתי רק מכתובת או שם משפחה*.
 *
 * So the two are separated. Relatives are the surname cluster inside the
 * address; everybody else at that address is shown as what they are, which for a
 * Chabad house is useful information in its own right — these are the neighbours
 * on the same staircase.
 */
const PERSON_FAMILY_ROLES: { id: string; label: string }[] = [
  { id: "father", label: "אבא" },
  { id: "mother", label: "אמא" },
  { id: "child", label: "ילד/ה" },
  { id: "spouse", label: "בן/בת זוג" },
];

function AddFamilyMemberControl({
  person,
  existingIds,
}: {
  person: Person;
  existingIds: string[];
}) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState("");
  const [myRole, setMyRole] = useState(
    person.householdRole === "father" ||
      person.householdRole === "mother" ||
      person.householdRole === "spouse" ||
      person.householdRole === "child"
      ? person.householdRole
      : person.isChild
        ? "child"
        : "father",
  );
  const [targetRole, setTargetRole] = useState("mother");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const search = usePeopleSearch(open ? term : "");
  const skip = new Set([person.id, ...existingIds]);

  async function linkPerson(other: Person) {
    setBusy(true);
    setErr(null);
    setMsg(null);
    try {
      await linkDuplicateAsFamily([person.id, other.id], {
        [person.id]: myRole,
        [other.id]: targetRole,
      });
      setMsg(`הוספנו את ${other.displayName ?? "איש הקשר"} למשפחה.`);
      setTerm("");
      setOpen(false);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "לא הצלחנו להוסיף למשפחה. נסו שוב.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ padding: "8px 14px 14px" }}>
      {msg && <div className="notice info" style={{ marginBottom: 8 }}>{msg}</div>}
      {err && <div className="notice error" style={{ marginBottom: 8 }}>{err}</div>}
      {!open ? (
        <button type="button" className="btn" onClick={() => setOpen(true)}>
          <IcPlus size={15} /> הוספת בן משפחה
        </button>
      ) : (
        <div
          style={{
            padding: 12,
            background: "var(--bg-soft)",
            border: "1px solid var(--line)",
          }}
        >
          <p className="faint" style={{ marginBottom: 6 }}>
            התפקיד של <b>{person.displayName}</b> במשפחה:
          </p>
          <div className="chips" style={{ marginBottom: 10 }}>
            {PERSON_FAMILY_ROLES.map((r) => (
              <button
                key={r.id}
                type="button"
                className={myRole === r.id ? "tagchip tagchip-on" : "tagchip"}
                onClick={() => setMyRole(r.id)}
                disabled={busy}
              >
                {r.label}
              </button>
            ))}
          </div>

          <p className="faint" style={{ marginBottom: 6 }}>
            התפקיד של בן המשפחה שאתם מוסיפים:
          </p>
          <div className="chips" style={{ marginBottom: 10 }}>
            {PERSON_FAMILY_ROLES.map((r) => (
              <button
                key={r.id}
                type="button"
                className={targetRole === r.id ? "tagchip tagchip-on" : "tagchip"}
                onClick={() => setTargetRole(r.id)}
                disabled={busy}
              >
                {r.label}
              </button>
            ))}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <div className="searchbar grow" style={{ margin: 0 }}>
              <IcSearch size={16} />
              <input
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                placeholder="חפשו בן משפחה לפי שם או טלפון…"
                autoFocus
              />
            </div>
            <button
              type="button"
              className="btn"
              onClick={() => {
                setOpen(false);
                setTerm("");
              }}
              disabled={busy}
            >
              סגור
            </button>
          </div>

          {term.trim().length > 0 && (
            <div>
              {search.data
                .filter((p) => !skip.has(p.id))
                .slice(0, 6)
                .map((p) => (
                  <div
                    key={p.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "6px 0",
                      borderTop: "1px solid var(--line)",
                    }}
                  >
                    <Avatar name={p.displayName ?? "?"} size={30} />
                    <span className="grow">
                      <span className="name-row">{p.displayName ?? "ללא שם"}</span>
                      <span className="faint mono" dir="ltr" style={{ display: "block", textAlign: "right" }}>
                        {primaryPhone(p) ?? "אין טלפון"}
                      </span>
                    </span>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => void linkPerson(p)}
                      disabled={busy}
                    >
                      <IcPlus size={14} /> הוסף למשפחה
                    </button>
                  </div>
                ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FamilyCard({
  person,
  household,
  members,
  error,
}: {
  person: Person;
  household: import("../data").Household | null;
  members: Person[];
  error: string | null;
}) {
  const [showNeighbours, setShowNeighbours] = useState(false);

  if (error) {
    return (
      <div className="card">
        <div className="card-title">משפחה</div>
        <div className="card-pad">
          <div className="notice error" style={{ margin: 0 }}>
            לא הצלחנו לטעון את המשפחה. זו תקלה. זה לא אומר שאין משפחה.
            <br />
            <span className="faint faint-xs mono">
              {error}
            </span>
          </div>
        </div>
      </div>
    );
  }

  if (!household) {
    return (
      <div className="card">
        <div className="card-title">משפחה</div>
        <div className="card-pad" style={{ paddingBottom: 4 }}>
          <p className="muted">לא משויך למשפחה.</p>
        </div>
        <AddFamilyMemberControl person={person} existingIds={[]} />
      </div>
    );
  }

  const isBuilding = household.kind === "address_group";
  const surname = person.familySurname ?? null;
  const excluded = new Set(person.familyExcludedPersonIds ?? []);
  const others = members.filter((m) => m.id !== person.id);

  const relatives = others.filter(
    (m) =>
      !excluded.has(m.id) &&
      (!isBuilding || (surname != null && (m.familySurname ?? null) === surname)),
  );
  const neighbours = others.filter((m) => !relatives.includes(m));

  const addressLabel = [household.address?.street, household.address?.houseNumber]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="card">
      <div className="card-title">{isBuilding ? "משפחה ושכנים" : "משפחה"}</div>

      {isBuilding ? (
        <Field
          label="כתובת משותפת"
          value={
            addressLabel
              ? `${addressLabel} · ${household.membersResolved ?? others.length + 1} אנשי קשר`
              : `${household.membersResolved ?? others.length + 1} אנשי קשר באותה כתובת`
          }
        />
      ) : (
        <Field
          label="משפחה"
          value={
            household.displayName
              ? `${household.displayName}${
                  household.membersResolved ? ` · ${household.membersResolved} בני משפחה` : ""
                }`
              : null
          }
        />
      )}

      {relatives.length > 0 ? (
        <div style={{ padding: "2px 14px 4px" }}>
          {relatives.map((m) => (
            <MemberRow key={m.id} person={m} separateFrom={person.id} />
          ))}
        </div>
      ) : (
        <div style={{ padding: "0 14px 4px" }}>
          <p className="muted">
            {isBuilding
              ? `אין בכתובת הזו עוד איש קשר בשם ${surname ?? ""}, אז אין את מי לשייך אליו כמשפחה.`
              : "אין עוד בני משפחה במערכת."}
          </p>
        </div>
      )}

      <AddFamilyMemberControl person={person} existingIds={relatives.map((r) => r.id)} />

      {(isBuilding || neighbours.length > 0) && (
        <div style={{ padding: "6px 14px 14px" }}>
          {isBuilding && (
            <div className="notice info" style={{ margin: "0 0 8px" }}>
              הקבוצה הזו הגיעה מהייבוא לפי <b>כתובת</b>, לא לפי משפחה. בבניין גרה יותר
              ממשפחה אחת, אז אנחנו מציגים כאן רק את מי שיש לו אותו שם משפחה.
            </div>
          )}

          {neighbours.length > 0 && (
            <>
              <button className="btn btn-wide" onClick={() => setShowNeighbours((v) => !v)}>
                {showNeighbours
                  ? "הסתירו"
                  : `הציגו ${neighbours.length} ${isBuilding ? "שכנים באותה כתובת" : "אנשי קשר שהופרדו"}`}
              </button>
              {showNeighbours && (
                <>
                  {neighbours.map((m) => (
                    <MemberRow key={m.id} person={m} linkTo={person.id} />
                  ))}
                  <p className="faint" style={{ marginTop: 6 }}>
                    „כן משפחה” מחזיר את איש הקשר לרשימת המשפחה, בשני הכרטיסים.
                  </p>
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * One person in the family card.
 *
 * `separateFrom` adds the manual override: relatives inside an address group are
 * worked out from a shared surname, and two unrelated families with the same
 * surname in one building — דודו כהן and ראם כהן — is the case no rule can
 * separate. Rare, so it is one small action rather than a screen, but a guess
 * nobody can correct is worse than no guess.
 *
 * `linkTo` is the same control the other way round, for a neighbour the guess
 * missed: a relative whose surname is written differently.
 */
function MemberRow({
  person,
  separateFrom,
  linkTo,
}: {
  person: Person;
  separateFrom?: string;
  linkTo?: string;
}) {
  const [busy, setBusy] = useState(false);
  const [asking, setAsking] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function decide(related: boolean, otherId: string) {
    setBusy(true);
    setErr(null);
    try {
      await setFamilyLink(otherId, person.id, related);
      setAsking(false);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "לא הצלחנו לבצע את הפעולה. נסו שוב.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="list-row" style={{ paddingInline: 0 }}>
        <Link
          to={`/people/${person.id}`}
          className="row grow"
          style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}
        >
          <Avatar name={person.displayName ?? "?"} size={30} />
          <span className="grow t-md">
            {person.displayName ?? "ללא שם"}
            {person.householdRole && (
              <span className="faint" style={{ display: "block", marginTop: 2 }}>
                {roleLabel(person.householdRole)}
              </span>
            )}
          </span>
        </Link>

        {separateFrom && !asking && (
          <button
            className="faint"
            onClick={() => setAsking(true)}
            disabled={busy}
            style={{ padding: "4px 6px", flexShrink: 0 }}
          >
            לא משפחה
          </button>
        )}
        {linkTo && !asking && (
          <button
            className="faint"
            onClick={() => decide(true, linkTo)}
            disabled={busy}
            style={{ padding: "4px 6px", flexShrink: 0 }}
          >
            {busy ? "…" : "כן משפחה"}
          </button>
        )}
        {!separateFrom && !linkTo && <IcChevron size={15} />}
      </div>

      {asking && separateFrom && (
        <div className="notice info" style={{ margin: "0 0 8px" }}>
          להפריד את <b>{person.displayName}</b> מהמשפחה הזו? הכתובת נשארת אותה כתובת,
          אבל הם כבר לא יופיעו כמשפחה. זה חל על שני הכרטיסים, לא רק על הכרטיס הזה.
          <div className="btn-row" style={{ marginTop: 8 }}>
            <button className="btn" onClick={() => decide(false, separateFrom)} disabled={busy}>
              {busy ? "שומרים…" : "כן, להפריד"}
            </button>
            <button className="btn" onClick={() => setAsking(false)} disabled={busy}>
              ביטול
            </button>
          </div>
        </div>
      )}

      {err && <div className="notice error">{err}</div>}
    </>
  );
}

/** The icon and tint say what kind of thing happened before the text is read. */
const KIND_STYLE: Record<string, { icon: React.ReactNode; bg: string; fg: string }> = {
  donation: { icon: <IcWallet size={16} />, bg: "var(--amber-soft)", fg: "var(--amber)" },
  attendance: { icon: <IcPeople size={16} />, bg: "var(--green-soft)", fg: "var(--green)" },
  call: { icon: <IcPhone size={16} />, bg: "var(--orange-soft)", fg: "var(--orange-2)" },
  whatsapp: { icon: <IcWhatsapp size={15} />, bg: "var(--green-soft)", fg: "var(--green)" },
  meeting: { icon: <IcHome size={16} />, bg: "var(--slate-soft)", fg: "var(--slate)" },
  visit: { icon: <IcHome size={16} />, bg: "var(--slate-soft)", fg: "var(--slate)" },
  registration: { icon: <IcCheck size={16} />, bg: "var(--green-soft)", fg: "var(--green)" },
  note: { icon: <IcClock size={16} />, bg: "var(--slate-soft)", fg: "var(--slate)" },
};

function TimelineRow({
  entry,
  onOpenSubmission,
}: {
  entry: TimelineEntry;
  onOpenSubmission: (submissionId: string) => void;
}) {
  const style = KIND_STYLE[entry.kind] ?? {
    icon: <IcCalendar size={16} />,
    bg: "var(--slate-soft)",
    fg: "var(--slate)",
  };

  /**
   * A row that came from a form on the website opens what the person filled in.
   *
   * Only those rows: a donation or a logged call has nothing further to show, and
   * a row that looks tappable and does nothing is worse than a plain one.
   */
  const submissionId = entry.sourceEventId;

  return (
    <div className="tl-item">
      <span className="tl-ico" style={{ background: style.bg, color: style.fg }}>
        {style.icon}
      </span>
      <div className="grow">
        <p className="muted" style={{ color: "var(--ink)", fontWeight: 500 }}>
          {entry.title}
        </p>
        {entry.subtitle && <p className="muted">{entry.subtitle}</p>}

        {submissionId && (
          <button
            className="faint"
            onClick={() => onOpenSubmission(submissionId)}
            style={{
              padding: "4px 0",
              textDecoration: "underline",
              color: "var(--orange-2)",
            }}
          >
            מה מילאו בטופס
          </button>
        )}

        {/* An open promise, on the entry that made it. */}
        {entry.followUpDueAt && (
          <p className="rec-badge rec-child" style={{ marginTop: 4 }}>
            משימה ל־
            {new Date(entry.followUpDueAt).toLocaleDateString("he-IL", {
              day: "numeric",
              month: "long",
            })}
          </p>
        )}

        <p className="faint">
          {entry.when}
          {entry.approximate && " · תאריך משוער"}
          {/* Says that the doubling in the source was merged, rather than
              leaving the reader to wonder why one purchase has two names. */}
          {entry.sourceRecordCount > 1 &&
            ` · נרשם ב־${entry.sourceRecordCount} מקורות`}
        </p>
        {entry.alsoRecordedAs.length > 0 && (
          <p className="faint">גם בשם: {entry.alsoRecordedAs.join(" · ")}</p>
        )}
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  mono,
}: {
  label: string;
  value: string | null | undefined;
  mono?: boolean;
}) {
  return (
    <div className="field">
      <span className="field-label">{label}</span>
      <span className={mono ? "field-value mono" : "field-value"}>{value || "—"}</span>
    </div>
  );
}

function addressLine(p: Person): string | null {
  const a = p.address;
  if (!a) return null;
  if (a.formatted) return a.formatted;
  const line = [a.street, a.houseNumber].filter(Boolean).join(" ");
  return [line, a.neighborhood, a.city].filter(Boolean).join(", ") || null;
}

function roleLabel(r: string): string {
  const map: Record<string, string> = {
    father: "אב",
    mother: "אם",
    child: "ילד/ה",
    spouse: "בן/בת זוג",
    בוגר: "בוגר",
  };
  return map[r] ?? r;
}

function sourceLabel(t?: string): string {
  if (t === "intake") return "הגיע מטופס באתר";
  if (t === "demo") return "נתונים לדוגמה";
  if (t === "manual") return "נוסף ידנית";
  if (t === "import" || t === "historical_import") return "ייבוא היסטורי";
  if (t === "google_contacts") return "מאנשי הקשר בגוגל";
  if (t === "kodeshboard") return "מקודשבורד";
  return "מקור לא ידוע";
}

export const TIMELINE_ICONS = { IcCalendar, IcWallet, IcAlert, IcHome };
